import type { PageObject } from './CustomPageCard.model';
import type { Academy } from '~/generated_types';
import { I18n } from '~/common/explicit-exports';
import AcademyCertificationCard from '~/components/academies/common/academy-certification-card';
import AcademyThemeContentCard from '~/components/academies/common/academy-theme-content-card';
import AcademyThemeCollectionCard from '~/components/academies/common/academy-theme-collection-card';
import { ObjectType } from './CustomPageCard.model';
import { CustomPageGuideOrResourceCard } from './CustomPageGuideOrResourceCard';

const TRANSLATED_VIEW = I18n.t('js.academy.content_card.view');
const { ACADEMY_EVENT } = window.APP_CONSTANTS.ASSET_PICKER_TYPES;

interface CustomPageCardProps {
  className?: string;
  pageObject: PageObject;
  academy: Academy['MINIMUM_THEME_CONTENT'];
  accessibleContentIds: string[];
  pageSlug: string;
  urlParams?: Record<string, string>;
}

export const CustomPageCard = ({
  pageSlug,
  pageObject,
  academy,
  accessibleContentIds,
  className,
  urlParams,
}: CustomPageCardProps) => {
  const { themeOptions } = academy;
  switch (pageObject.objectType) {
    case ObjectType.path:
    case ObjectType.certification: {
      const certification = pageObject.learnerObject;
      const certUser = certification.academyCertificationUser;
      return (
        <AcademyCertificationCard
          className={className}
          accessibleContentIds={accessibleContentIds}
          academyCertificationUser={certUser}
          awarded={certUser?.awarded}
          awardedAt={certUser?.awardedAt}
          canRecertify={certUser?.canRecertify}
          certification={certification}
          certificationAssigned={certUser?.internalAssignerId}
          completed={certUser?.completed}
          dueAt={certUser?.dueAt}
          expiresAt={certUser?.expiresAt}
          isEcommerceSetUp={academy.isEcommerceSetUp}
          pageSlug={pageSlug}
          themeOptions={themeOptions}
        />
      );
    }

    case ObjectType.collection: {
      const collection = pageObject.learnerObject;
      if (themeOptions.collectionCardVersion === 1) {
        return (
          <AcademyThemeContentCard
            className={className}
            accentColor={themeOptions.accentColor}
            bannerImage={collection.bannerImage}
            cardStyle={themeOptions.cardStyle}
            description={collection.description}
            footerContentType="item"
            name={collection.name}
            numContent={collection.numContent}
            contentId={collection.id}
            url={pageSlug ? `/pages/${pageSlug}/collections/${collection.id}` : `/collections/${collection.id}`}
            isCollection
          />
        );
      }
      return (
        <AcademyThemeCollectionCard
          className={className}
          key={collection.id}
          bannerImage={collection.bannerImage}
          description={collection.description}
          name={collection.name}
          numContent={collection.numContent}
          collectionId={collection.id}
          url={pageSlug ? `/pages/${pageSlug}/collections/${collection.id}` : `/collections/${collection.id}`}
        />
      );
    }
    case ObjectType.event: {
      const event = pageObject.learnerObject;
      const TRANSLATED_NUM_SESSIONS = I18n.t('js.academy.events.num_session', { count: event.numAvailableSessions });
      const { cardStyle, contentLabelOverrides } = academy.themeOptions;
      return (
        <AcademyThemeContentCard
          className={className}
          bannerImage={event.bannerImage}
          description={event.description}
          cardStyle={cardStyle}
          metasRight={TRANSLATED_NUM_SESSIONS}
          name={event.title}
          numContent={event.numAvailableSessions}
          progressText={TRANSLATED_VIEW}
          type={ACADEMY_EVENT}
          url={pageSlug ? `/pages/${pageSlug}/events/${event.id}` : `/events/${event.id}`}
          hasCardMetas
          contentLabelOverrides={contentLabelOverrides}
          contentId={event.id}
        />
      );
    }

    case ObjectType.guide:
    case ObjectType.resource:
    case ObjectType.scorm:
      return (
        <CustomPageGuideOrResourceCard
          className={className}
          academy={academy}
          pageObject={pageObject}
          accessibleContentIds={accessibleContentIds}
          pageSlug={pageSlug}
          urlParams={urlParams}
        />
      );
    default:
      return null;
  }
};
